import { useState, useMemo, useEffect, useRef } from 'react';
import { useApp } from '../context/AppContext';
import StarField from '../components/StarField';
import OrbitCanvas from '../components/OrbitCanvas';
import Oracle from '../components/Oracle';
import { archetypes } from '../data/archetypes';
import { questions } from '../data/questions';
import { calculateSynastry, generateDescription } from '../data/synastry';
import { encodeAnswers, decodeAnswers, rebuildScores, getTopArchetypes } from '../utils/codeUtils';
import {
  StarDust,
  AuroraRings,
  GridOverlay,
  CornerDecorations,
} from '../components/CosmicEffects';
import styles from './Explore.module.css';

// 预设测试好友 - 不同性格类型的代表
const testFriends = [
  { name: '小英 (英雄型)', code: 'CQKA', desc: '行动派，正义感强' },
  { name: '小智 (智者型)', code: 'BFDA', desc: '理性分析，追求真理' },
  { name: '小爱 (情人型)', code: 'DKCA', desc: '重感情，追求连接' },
  { name: '小叛 (叛逆者)', code: 'JRBA', desc: '打破规则，质疑权威' },
  { name: '小创 (创造者)', code: 'GNEA', desc: '创意无限，独特视角' },
  { name: '小护 (照顾者)', code: 'CLDA', desc: '温暖体贴，乐于付出' },
];

export default function Explore() {
  const { state, dispatch } = useApp();
  const [selectedArchetype, setSelectedArchetype] = useState(null);
  const [hoveredPlanet, setHoveredPlanet] = useState(null);
  const [mode, setMode] = useState('explore'); // 'explore' | 'synastry'
  const [friendCode, setFriendCode] = useState('');
  const [synastryResult, setSynastryResult] = useState(null);
  const [codeError, setCodeError] = useState('');
  const [codeCopied, setCodeCopied] = useState(false);
  const [showOracle, setShowOracle] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const containerRef = useRef(null);

  // 入场动画
  useEffect(() => {
    const timer = setTimeout(() => setShowIntro(false), 800);
    return () => clearTimeout(timer);
  }, []);

  // 获取用户主要原型
  const userPrimary = useMemo(() => {
    if (!state.scores || Object.keys(state.scores).length === 0) return null;
    const sorted = Object.entries(state.scores).sort((a, b) => b[1] - a[1]);
    const key = sorted[0]?.[0];
    return key ? { key, info: archetypes[key] } : null;
  }, [state.scores]);
  // 生成当前用户的代码
  const myCode = state.answers.length === 10 ? encodeAnswers(state.answers) : null;

  const handleBack = () => {
    dispatch({ type: 'SET_PAGE', payload: 'landing' });
  };

  const handlePlanetClick = (key) => {
    setSelectedArchetype(key === selectedArchetype ? null : key);
  };

  const handleCopyCode = () => {
    if (myCode) {
      navigator.clipboard.writeText(myCode);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    }
  };

  const handleCompare = () => {
    setCodeError('');
    const code = friendCode.trim().toUpperCase();
    
    if (code.length !== 4) {
      setCodeError('请输入4位代码');
      return;
    }
    
    const friendIndices = decodeAnswers(code);
    if (!friendIndices) {
      setCodeError('代码无效，请检查');
      return;
    }
    
    const friendScores = rebuildScores(friendIndices, questions);
    const synastry = calculateSynastry(state.scores, friendScores);
    const description = generateDescription(synastry, archetypes);
    
    setSynastryResult({
      ...synastry,
      ...description,
      friendScores,
    });
  };

  const handleCloseSynastry = () => {
    setSynastryResult(null);
    setFriendCode('');
  };

  const uniquePlanetLayout = [
    { key: 'magician', x: 8, y: 12, size: 100 },
    { key: 'creator', x: 22, y: 18, size: 85 },
    { key: 'outlaw', x: 52, y: 8, size: 75 },
    { key: 'explorer', x: 68, y: 22, size: 70 },
    { key: 'hero', x: 78, y: 35, size: 90 },
    { key: 'ruler', x: 12, y: 45, size: 130 },
    { key: 'innocent', x: 32, y: 38, size: 75 },
    { key: 'everyman', x: 48, y: 48, size: 70 },
    { key: 'sage', x: 72, y: 55, size: 120 },
    { key: 'caregiver', x: 8, y: 72, size: 110 },
    { key: 'lover', x: 28, y: 78, size: 105 },
    { key: 'jester', x: 48, y: 85, size: 65 },
  ];

  const selected = selectedArchetype ? archetypes[selectedArchetype] : null;
  const myTop = state.scores ? getTopArchetypes(state.scores, 1)[0] : null;

  return (
    <div className={`${styles.container} ${showIntro ? styles.intro : ''}`} ref={containerRef}>
      <StarField density={80} />
      <GridOverlay />
      <AuroraRings />
      <StarDust count={30} />
      <CornerDecorations />
      
      <button className={styles.backBtn} onClick={handleBack}>
        ← 返回首页
      </button>

      {/* 模式切换 */}
      <div className={styles.modeSwitch}>
        <button 
          className={`${styles.modeBtn} ${mode === 'explore' ? styles.active : ''}`}
          onClick={() => { setMode('explore'); setSynastryResult(null); }}
        >
          探索星图
        </button>
        <button 
          className={`${styles.modeBtn} ${mode === 'synastry' ? styles.active : ''}`}
          onClick={() => setMode('synastry')}
        >
          星球比对
        </button>
      </div>

      {mode === 'explore' ? (
        <>
          <h1 className={styles.pageTitle}>探索星图</h1>
          <p className={styles.pageSubtitle}>点击星球查看12原型详细解读</p>

          <div className={styles.galaxyArea}>
            <div className={styles.orbit} style={{ width: '300px', height: '180px' }} />
            <div className={styles.orbit} style={{ width: '480px', height: '280px' }} />
            <div className={styles.orbit} style={{ width: '660px', height: '380px' }} />
            <div className={styles.orbit} style={{ width: '840px', height: '480px' }} />
            <div className={styles.orbit} style={{ width: '1020px', height: '580px' }} />
            
            <div className={styles.centerStar} />

            {uniquePlanetLayout.map(({ key, x, y, size }) => {
              const arch = archetypes[key];
              const isSelected = selectedArchetype === key;
              const isHovered = hoveredPlanet === key;
              
              return (
                <div
                  key={key}
                  className={`${styles.planet} ${isSelected ? styles.selected : ''}`}
                  style={{ left: `${x}%`, top: `${y}%` }}
                  onClick={() => handlePlanetClick(key)}
                  onMouseEnter={() => setHoveredPlanet(key)}
                  onMouseLeave={() => setHoveredPlanet(null)}
                >
                  {(isSelected || isHovered) && (
                    <div 
                      className={styles.glow}
                      style={{ 
                        width: `${size + 40}px`, 
                        height: `${size + 40}px`,
                        background: `radial-gradient(circle, ${arch.color}60 0%, transparent 70%)`
                      }}
                    />
                  )}
                  <img 
                    src={arch.planetImg} 
                    alt={arch.planet}
                    style={{ 
                      width: `${size}px`,
                      transform: isSelected || isHovered ? 'scale(1.12)' : 'scale(1)',
                    }}
                  />
                  <span className={styles.label}>{arch.name}</span>
                </div>
              );
            })}
          </div>

          <div className={`${styles.panel} ${selected ? styles.visible : ''}`}>
            {selected ? (
              <>
                <button 
                  className={styles.panelClose} 
                  onClick={() => setSelectedArchetype(null)}
                >
                  ✕
                </button>
                <div className={styles.panelHeader}>
                  <img src={selected.planetImg} alt="" />
                  <div>
                    <h2>{selected.name}</h2>
                    <span>{selected.planet}</span>
                  </div>
                </div>
                <div className={styles.section}>
                  <h3>星象特征</h3>
                  <p>{selected.feature}</p>
                </div>
                <div className={styles.section}>
                  <h3>异化表现</h3>
                  <p>{selected.alienation}</p>
                </div>
                <div className={styles.section}>
                  <h3>疗愈处方</h3>
                  <ul className={styles.vacationList}>
                    {selected.vacation.split('。').filter(item => item.trim()).map((item, index) => (
                      <li key={index}>{item.trim()}。</li>
                    ))}
                  </ul>
                </div>
              </>
            ) : (
              <div className={styles.placeholder}>
                <p>✦</p>
                <p>点击星球探索原型</p>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <h1 className={styles.pageTitle}>星球比对</h1>
          <p className={styles.pageSubtitle}>输入好友代码，探索你们的引力关系</p>

          {!synastryResult ? (
            <div className={`${styles.synastryInput} ${inputFocused ? styles.focused : ''}`}>
              {/* 角落装饰 */}
              <div className={styles.cornerDecor} />
              <div className={styles.cornerDecorBR} />
              
              {/* 装饰光环 */}
              <div className={styles.inputGlow} />
              
              {/* 顶部装饰图标 */}
              <div className={styles.inputIcon}>
                <span>✦</span>
              </div>

              {myCode ? (
                <>
                  <div className={styles.myCodeSection}>
                    <span className={styles.codeLabel}>你的星球代码</span>
                    <div className={styles.codeDisplay}>
                      <div className={styles.codeWrapper}>
                        {myCode.split('').map((char, i) => (
                          <span key={i} className={styles.codeChar} style={{ animationDelay: `${i * 0.1}s` }}>
                            {char}
                          </span>
                        ))}
                      </div>
                      <button className={styles.copyBtn} onClick={handleCopyCode}>
                        {codeCopied ? '已复制 ✓' : '复制'}
                      </button>
                    </div>
                    <p className={styles.codeHint}>分享给好友，让TA输入你的代码</p>
                  </div>

                  <div className={styles.divider}>
                    <div className={styles.dividerStar}>◇</div>
                  </div>

                  <div className={styles.friendCodeSection}>
                    <span className={styles.codeLabel}>输入好友代码</span>
                    <div className={styles.inputRow}>
                      <div className={styles.inputWrapper}>
                        <input
                          type="text"
                          value={friendCode}
                          onChange={(e) => setFriendCode(e.target.value.toUpperCase().slice(0, 4))}
                          onFocus={() => setInputFocused(true)}
                          onBlur={() => setInputFocused(false)}
                          placeholder="4位代码"
                          maxLength={4}
                          className={styles.codeInput}
                        />
                        <div className={styles.inputUnderline} />
                      </div>
                      <button 
                        className={styles.compareBtn}
                        onClick={handleCompare}
                        disabled={friendCode.length !== 4}
                      >
                        <span>开始解析</span>
                        <span className={styles.btnArrow}>→</span>
                      </button>
                    </div>
                    {codeError && <p className={styles.error}>{codeError}</p>}
                  </div>

                  {/* 测试用预设好友 */}
                  <div className={styles.testFriends}>
                    <span className={styles.testLabel}>🧪 测试用虚拟好友</span>
                    <div className={styles.friendList}>
                      {testFriends.map((friend, index) => (
                        <button
                          key={friend.code}
                          className={styles.friendBtn}
                          onClick={() => setFriendCode(friend.code)}
                          title={friend.desc}
                          style={{ animationDelay: `${index * 0.1}s` }}
                        >
                          <div className={styles.friendAvatar}>
                            {friend.name.charAt(0)}
                          </div>
                          <div className={styles.friendInfo}>
                            <span className={styles.friendName}>{friend.name}</span>
                            <span className={styles.friendCode}>{friend.code}</span>
                          </div>
                          <span className={styles.friendDesc}>{friend.desc}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <div className={styles.noCode}>
                  <div className={styles.noCodeIcon}>✦</div>
                  <p>你还没有完成测试</p>
                  <p className={styles.noCodeHint}>完成测试后即可获得专属星球代码</p>
                  <button 
                    className={styles.goTestBtn}
                    onClick={() => dispatch({ type: 'SET_PAGE', payload: 'consent' })}
                  >
                    开始测试 →
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className={styles.synastryResult}>
              <button className={styles.closeBtn} onClick={handleCloseSynastry}>
                ← 返回
              </button>

              {/* 背景粒子 */}
              <div className={styles.resultParticles}>
                {Array.from({ length: 20 }).map((_, i) => (
                  <div key={i} className={styles.particle} style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 5}s`,
                    animationDuration: `${3 + Math.random() * 4}s`
                  }} />
                ))}
              </div>

              {/* 三栏布局 */}
              <div className={styles.resultLayout}>
                {/* 左侧 - 你的星球 */}
                <div className={styles.planetSide}>
                  <div className={styles.planetCard}>
                    <div className={styles.planetGlow} style={{ background: archetypes[synastryResult.topA[0]]?.color }} />
                    <img src={archetypes[synastryResult.topA[0]]?.planetImg} alt="" className={styles.planetImg} />
                    <h3>{archetypes[synastryResult.topA[0]]?.name}</h3>
                    <span className={styles.planetLabel}>{archetypes[synastryResult.topA[0]]?.planet}</span>
                    <p className={styles.roleTag}>你</p>
                  </div>
                </div>

                {/* 中间 - 轨道动画 + 契合度 */}
                <div className={styles.centerSection}>
                  <div className={styles.compatDisplay}>
                    <div className={styles.compatRing}>
                      <svg viewBox="0 0 100 100" className={styles.compatSvg}>
                        <circle cx="50" cy="50" r="45" className={styles.compatBg} />
                        <circle 
                          cx="50" cy="50" r="45" 
                          className={styles.compatProgress}
                          style={{ 
                            strokeDasharray: `${synastryResult.compatibility * 2.83} 283`,
                          }} 
                        />
                      </svg>
                      <div className={styles.compatInner}>
                        <span className={styles.compatNum}>{synastryResult.compatibility}</span>
                        <span className={styles.compatLabel}>契合度</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className={styles.orbitArea}>
                    <OrbitCanvas
                      planetA={synastryResult.topA[0]}
                      planetB={synastryResult.topB[0]}
                      compatibility={synastryResult.compatibility}
                      archetypes={archetypes}
                    />
                  </div>

                  <div className={styles.resultInfo}>
                    <div className={styles.resultTitleWrapper}>
                      <div className={styles.resultTitleDecor}>◇ ◇ ◇</div>
                      <h2 className={styles.resultTitle}>{synastryResult.title}</h2>
                    </div>
                    <p className={styles.resultDesc}>{synastryResult.desc}</p>
                    
                    {/* 契合点 */}
                    {synastryResult.strengths && synastryResult.strengths.length > 0 && (
                      <div className={styles.relationSection}>
                        <h4 className={styles.relationTitle}>
                          <span className={styles.relationIcon}>✦</span>
                          契合点
                        </h4>
                        <ul className={styles.relationList}>
                          {synastryResult.strengths.map((item, i) => (
                            <li key={i} style={{ animationDelay: `${i * 0.15}s` }}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* 冲突点 */}
                    {synastryResult.challenges && synastryResult.challenges.length > 0 && (
                      <div className={styles.relationSection}>
                        <h4 className={styles.relationTitle}>
                          <span className={styles.relationIcon}>⚡</span>
                          需要注意
                        </h4>
                        <ul className={styles.relationList}>
                          {synastryResult.challenges.map((item, i) => (
                            <li key={i} style={{ animationDelay: `${i * 0.15}s` }}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    <div className={styles.similarity}>
                      <span>相似度</span>
                      <div className={styles.simBar}>
                        <div className={styles.simFill} style={{ width: `${synastryResult.similarity}%` }} />
                        <div className={styles.simGlow} style={{ left: `${synastryResult.similarity}%` }} />
                      </div>
                      <span className={styles.simValue}>{synastryResult.similarity}%</span>
                    </div>
                  </div>
                </div>

                {/* 右侧 - TA的星球 */}
                <div className={styles.planetSide}>
                  <div className={styles.planetCard}>
                    <div className={styles.planetGlow} style={{ background: archetypes[synastryResult.topB[0]]?.color }} />
                    <img src={archetypes[synastryResult.topB[0]]?.planetImg} alt="" className={styles.planetImg} />
                    <h3>{archetypes[synastryResult.topB[0]]?.name}</h3>
                    <span className={styles.planetLabel}>{archetypes[synastryResult.topB[0]]?.planet}</span>
                    <p className={styles.roleTag}>TA</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* 星际祭司按钮 - 固定在右下角 */}
      {userPrimary && (
        <button className={styles.oracleBtn} onClick={() => setShowOracle(true)}>
          ✦ 星际祭司
        </button>
      )}

      {/* 星际祭司对话框 */}
      {showOracle && userPrimary && (
        <Oracle
          archetype={userPrimary.key}
          archetypeInfo={userPrimary.info}
          onClose={() => setShowOracle(false)}
        />
      )}
    </div>
  );
}
